import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Heart, Sparkles, MessageCircle, Bookmark, TrendingUp, Briefcase, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const Dashboard = () => {
  const { user, profile } = useAuth();
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      if (profile?.role === 'entrepreneur') {
        const { data: businesses } = await supabase.from('businesses').select('*').eq('owner_id', user.id);
        const ids = businesses?.map(b => b.id) || [];

        let sparksReceived = 0, messages = 0;
        if (ids.length) {
          const { count: sc } = await supabase.from('sparks').select('id', { count: 'exact', head: true }).in('business_id', ids);
          sparksReceived = sc || 0;
          const { count: mc } = await supabase.from('messages').select('id', { count: 'exact', head: true });
          messages = mc || 0;
        }
        const totalViews = (businesses || []).reduce((s,b) => s + (b.view_count || 0), 0);
        const totalLikes = (businesses || []).reduce((s,b) => s + (b.like_count || 0), 0);
        setStats({ businesses: businesses?.length || 0, sparksReceived, messages, totalViews, totalLikes });
        setItems(businesses || []);
      } else {
        const { count: sparksSent } = await supabase.from('sparks').select('id', { count: 'exact', head: true }).eq('investor_id', user.id);
        const { count: accepted } = await supabase.from('sparks').select('id', { count: 'exact', head: true }).eq('investor_id', user.id).eq('status', 'accepted');
        const { count: saved } = await supabase.from('likes').select('id', { count: 'exact', head: true }).eq('user_id', user.id);
        setStats({ sparksSent: sparksSent||0, accepted: accepted||0, saved: saved||0 });
        const { data: sparks } = await supabase.from('sparks')
          .select('*, business:businesses(*)')
          .eq('investor_id', user.id).order('created_at', { ascending: false }).limit(10);
        setItems(sparks || []);
      }
    };
    load();
  }, [user, profile]);

  const isEntrep = profile?.role === 'entrepreneur';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display text-2xl font-bold">Meu Painel</h1>
          <p className="text-xs text-slate-400 mt-0.5">{isEntrep ? 'Como sua ideia está performando' : 'Suas Sparks e oportunidades'}</p>
        </div>
        {isEntrep && (
          <Link to="/app/business/new" className="btn-gold rounded-xl px-4 py-2 text-xs font-bold flex items-center gap-1.5">
            <Plus size={13}/> Novo negócio
          </Link>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {isEntrep ? (
          <>
            <Metric icon={Eye} label="Visualizações" value={stats.totalViews||0} color="blue" />
            <Metric icon={Heart} label="Curtidas" value={stats.totalLikes||0} color="gold" />
            <Metric icon={Sparkles} label="Sparks recebidas" value={stats.sparksReceived||0} color="gold" featured />
            <Metric icon={MessageCircle} label="Mensagens" value={stats.messages||0} color="blue" />
          </>
        ) : (
          <>
            <Metric icon={Sparkles} label="Sparks enviadas" value={stats.sparksSent||0} color="blue" featured />
            <Metric icon={TrendingUp} label="Aceitas" value={stats.accepted||0} color="gold" />
            <Metric icon={Bookmark} label="Salvos" value={stats.saved||0} color="blue" />
            <Metric icon={Briefcase} label="Em chat" value={stats.accepted||0} color="gold" />
          </>
        )}
      </div>

      {/* Items list */}
      <div className="mb-3 text-[11px] uppercase tracking-widest text-slate-400">{isEntrep ? 'Meus negócios' : 'Minhas Sparks recentes'}</div>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="glass-card rounded-2xl p-8 text-center text-sm text-slate-400">
            {isEntrep ? 'Você ainda não publicou nada.' : 'Você ainda não enviou nenhuma Spark.'}
          </div>
        )}
        {items.map((it) => isEntrep ? (
          <div key={it.id} className="glass-card rounded-2xl p-3 flex items-center gap-3">
            {it.cover_image_url && <img src={it.cover_image_url} alt="" className="w-14 h-14 rounded-xl object-cover"/>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{it.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{it.city || ''} {it.is_seeking && '· Procurando Spark'}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Meta</div>
              <div className="text-xs font-bold gradient-text-gold">R$ {Math.round((it.funding_target||0)/1000)}k</div>
            </div>
          </div>
        ) : (
          <div key={it.id} className="glass-card rounded-2xl p-3 flex items-center gap-3">
            {it.business?.cover_image_url && <img src={it.business.cover_image_url} alt="" className="w-14 h-14 rounded-xl object-cover"/>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{it.business?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">Status: {it.status}</div>
            </div>
            {it.status === 'accepted' && (
              <Link to={`/app/chat/${it.id}`} className="text-[11px] text-[#4facfe] hover:text-white">Conversar →</Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color, featured }) => {
  const isGold = color === 'gold';
  return (
    <motion.div whileHover={{ y:-3 }} className={`rounded-2xl p-4 ${featured ? 'spotlight-card' : ''}`}
      style={{
        background: featured ? (isGold?'linear-gradient(135deg, rgba(255,167,38,0.15), rgba(10,14,30,0.5))':'linear-gradient(135deg, rgba(79,172,254,0.15), rgba(10,14,30,0.5))') : 'rgba(255,255,255,0.03)',
        border: `1px solid ${featured?(isGold?'rgba(255,167,38,0.3)':'rgba(79,172,254,0.3)'):'rgba(255,255,255,0.08)'}`,
      }}>
      <div className="flex items-center justify-between mb-1.5">
        <Icon size={14} className={isGold?'text-[#ffd54f]':'text-[#4facfe]'}/>
        {featured && <Sparkles size={11} className="text-[#ffd54f]"/>}
      </div>
      <div className={`text-2xl heading-display font-bold ${isGold?'gradient-text-gold':'gradient-text-blue'}`}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{label}</div>
    </motion.div>
  );
};

export default Dashboard;
